import { useCallback, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarClock, CheckCircle2, Keyboard, LogIn, LogOut, MapPin, QrCode, ScanLine } from 'lucide-react';
import { getClockinHistory, getClockinToday, postClockinOut, postClockinScan } from '@/api/common';
import { errMsg, unwrap } from '@/components/kit/data';
import { QrScanner } from '@/components/ui/QrScanner';

interface Rec { date: string; clock_in_at: string | null; clock_out_at: string | null; status: string; minutes_late?: number; event?: string | null; fine?: number; hours?: number | null }
interface Today { date: string; now: string; timezone: string; rules?: { work_starts_at: string; grace_minutes: number; work_ends_at: string; geo_required?: boolean }; record: Rec | null; events?: Rec[]; next: 'clock_in' | 'clock_out' | 'done' }
interface History { month: string; summary: { days_present: number; late: number; minutes_late: number; absent: number; excused: number }; days: Rec[] }

const TONE: Record<string, string> = { on_time: 'bg-green-50 text-green-700', late: 'bg-amber-50 text-amber-800', absent: 'bg-red-50 text-red-700', excused: 'bg-blue-50 text-blue-700' };
const LABEL: Record<string, string> = { on_time: 'On time', late: 'Late', absent: 'Absent', excused: 'Excused' };
const Pill = ({ s }: { s: string }) => <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${TONE[s] ?? 'bg-slate-100 text-slate-600'}`}>{LABEL[s] ?? s}</span>;

const where = () => new Promise<{ lat: number; lng: number } | null>((res) => {
  if (!navigator.geolocation) return res(null);
  navigator.geolocation.getCurrentPosition((p) => res({ lat: p.coords.latitude, lng: p.coords.longitude }), () => res(null), { timeout: 6000, enableHighAccuracy: true });
});

/**
 * Clock in / out for every staff member (teacher, desk officer, bursar). Tap Scan, point the camera at the QR on the board
 * (the daily code or an event's code — assembly, a class period, a staff meeting). Typing the code still works.
 */
export function ClockInPage() {
  const qc = useQueryClient();
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [scan, setScan] = useState<null | 'clock_in' | 'clock_out'>(null);
  const [typing, setTyping] = useState(false);
  const [code, setCode] = useState('');
  const today = useQuery({ queryKey: ['clock-today'], queryFn: () => getClockinToday().then((r) => unwrap<Today>(r)) });
  const hist = useQuery({ queryKey: ['clock-history', month], queryFn: () => getClockinHistory({ month }).then((r) => unwrap<History>(r)) });

  const act = useMutation({
    mutationFn: async ({ kind, text }: { kind: 'clock_in' | 'clock_out'; text: string }) => {
      const pos = await where();
      const body = { code: text.trim(), ...(pos ?? {}) };
      return kind === 'clock_in' ? postClockinScan(body) : postClockinOut(body);
    },
    onSuccess: (r) => {
      setMsg({ ok: true, text: (r.data as { message?: string }).message ?? 'Done.' }); setCode(''); setTyping(false);
      ['clock-today', 'clock-history', 'teacher-home'].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
    },
    onError: (e) => setMsg({ ok: false, text: errMsg(e) }),
  });

  const t = today.data;
  const onCode = useCallback((text: string) => { const k = scan ?? 'clock_in'; setScan(null); setMsg(null); act.mutate({ kind: k, text }); }, [scan, act]);
  // after the day's clock-in, an event QR can still be scanned (assembly, a class period)
  const kind: 'clock_in' | 'clock_out' = t?.next === 'clock_out' ? 'clock_out' : 'clock_in';
  const s = hist.data?.summary;

  return (
    <div className="max-w-3xl mx-auto space-y-4">
      <div><p className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Me</p><h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900">Clock in / out</h1>
        <p className="text-sm text-slate-500">Scan the QR code on the school board or at the event with your phone camera.</p></div>

      {msg && <p className={`text-sm rounded-xl px-3 py-2.5 ${msg.ok ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-700'}`}>{msg.text}</p>}

      <section className="bg-[#0A2540] text-white rounded-3xl p-5 sm:p-6">
        {today.isLoading ? <p className="text-sm text-white/70">Loading…</p> : !t ? <p className="text-sm text-white/80">Could not load today. Pull to refresh.</p> : (
          <>
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-xs text-white/60">{new Date(t.date + 'T12:00:00').toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' })}</p>
                <p className="text-4xl font-extrabold tracking-tight mt-1">{t.now}</p>
                {t.rules && <p className="text-xs text-white/60 mt-1">Work hours {t.rules.work_starts_at} – {t.rules.work_ends_at} · {t.rules.grace_minutes} min grace{t.rules.geo_required ? ' · location checked' : ''}</p>}
              </div>
              {t.record && <Pill s={t.record.status} />}
            </div>
            <div className="grid grid-cols-2 gap-2 mt-4">
              <div className="rounded-2xl bg-white/10 p-3"><p className="text-[11px] text-white/60">Clocked in</p><p className="text-lg font-bold">{t.record?.clock_in_at ?? '—'}</p>{!!t.record?.minutes_late && <p className="text-[11px] text-amber-300">{t.record.minutes_late} min late</p>}</div>
              <div className="rounded-2xl bg-white/10 p-3"><p className="text-[11px] text-white/60">Clocked out</p><p className="text-lg font-bold">{t.record?.clock_out_at ?? '—'}</p></div>
            </div>
            <div className="mt-4 space-y-2">
              {t.next === 'done' && <p className="text-sm text-green-300 inline-flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4" />You are done for today. You can still scan an event's code.</p>}
              <button disabled={act.isPending} onClick={() => { setMsg(null); setScan(kind); }} className="w-full py-4 rounded-2xl bg-green-500 hover:bg-green-400 text-white font-extrabold text-base inline-flex items-center justify-center gap-2 disabled:opacity-50">
                <ScanLine className="w-5 h-5" />{act.isPending ? 'Please wait…' : t.next === 'clock_out' ? 'Scan to clock out' : t.next === 'done' ? 'Scan an event code' : 'Scan to clock in'}
              </button>
              {t.next === 'clock_out' && <button disabled={act.isPending} onClick={() => { setMsg(null); setScan('clock_in'); }} className="w-full py-2.5 rounded-2xl bg-white/10 text-sm font-semibold inline-flex items-center justify-center gap-2"><QrCode className="w-4 h-4" />Scan an event code</button>}
              <button onClick={() => setTyping(!typing)} className="w-full text-xs text-white/70 inline-flex items-center justify-center gap-1.5 py-1"><Keyboard className="w-3.5 h-3.5" />{typing ? 'Hide' : 'Camera not working? Type the code'}</button>
              {typing && (
                <form className="flex gap-2" onSubmit={(e) => { e.preventDefault(); if (code.trim()) { setMsg(null); act.mutate({ kind, text: code }); } }}>
                  <input value={code} onChange={(e) => setCode(e.target.value)} placeholder="Code under the QR" className="flex-1 min-w-0 rounded-xl px-3 py-2.5 text-sm text-slate-900" />
                  <button disabled={!code.trim() || act.isPending} className="px-4 rounded-xl bg-white text-[#0A2540] text-sm font-bold disabled:opacity-50 inline-flex items-center gap-1">{kind === 'clock_out' ? <LogOut className="w-4 h-4" /> : <LogIn className="w-4 h-4" />}{kind === 'clock_out' ? 'Out' : 'In'}</button>
                </form>
              )}
            </div>
            {t.rules?.geo_required && <p className="text-[11px] text-white/60 mt-2 inline-flex items-center gap-1"><MapPin className="w-3 h-3" />Allow location when asked — the school checks you are on site.</p>}
          </>
        )}
      </section>

      {!!t?.events?.length && (
        <section className="bg-white rounded-2xl border border-slate-200">
          <h2 className="font-bold text-slate-900 px-4 pt-3.5 pb-2">Events today</h2>
          <ul className="divide-y divide-slate-100">
            {t.events.map((e, i) => (
              <li key={i} className="px-4 py-2.5 flex items-center gap-3">
                <CalendarClock className="w-4 h-4 text-slate-400 shrink-0" />
                <div className="flex-1 min-w-0"><p className="text-sm font-semibold text-slate-900 truncate">{e.event}</p><p className="text-xs text-slate-500">{e.clock_in_at ? `Scanned ${e.clock_in_at}` : 'Not scanned'}{e.minutes_late ? ` · ${e.minutes_late} min late` : ''}{e.fine ? ` · fine ₦${Number(e.fine).toLocaleString()}` : ''}</p></div>
                <Pill s={e.status} />
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className="bg-white rounded-2xl border border-slate-200">
        <div className="flex items-center justify-between gap-3 px-4 pt-3.5 pb-2"><h2 className="font-bold text-slate-900">My month</h2>
          <input type="month" className="border border-slate-300 rounded-xl px-3 py-1.5 text-sm" value={month} onChange={(e) => setMonth(e.target.value)} /></div>
        {s && (
          <div className="grid grid-cols-4 gap-2 px-4 pb-3">
            {[['Present', s.days_present, 'text-slate-900'], ['Late', s.late, 'text-amber-700'], ['Min late', s.minutes_late, 'text-amber-700'], ['Absent', s.absent, 'text-red-600']].map(([l, v, c]) => (
              <div key={l as string} className="rounded-xl bg-slate-50 py-2 text-center"><p className="text-[10px] text-slate-500">{l}</p><p className={`font-extrabold ${c}`}>{v as number}</p></div>
            ))}
          </div>
        )}
        {hist.isLoading ? <p className="px-4 pb-4 text-sm text-slate-500">Loading…</p> : !hist.data?.days?.length ? <p className="px-4 pb-5 text-sm text-slate-500">No records this month.</p> : (
          <ul className="divide-y divide-slate-100 border-t border-slate-100">
            {hist.data.days.map((d) => (
              <li key={d.date} className="px-4 py-2.5 flex items-center gap-3 text-sm">
                <span className="w-24 shrink-0 text-slate-700">{new Date(d.date + 'T12:00:00').toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })}</span>
                <span className="flex-1 text-slate-500">{d.clock_in_at ?? '—'} → {d.clock_out_at ?? '—'}{d.hours ? ` · ${d.hours} h` : ''}</span>
                <Pill s={d.status} />
              </li>
            ))}
          </ul>
        )}
      </section>

      {scan && <QrScanner title={scan === 'clock_out' ? 'Scan to clock out' : 'Scan to clock in'} onCode={onCode} onClose={() => setScan(null)} />}
    </div>
  );
}
